import { Injectable } from '@angular/core';
import { Subject, BehaviorSubject } from 'rxjs';

import { ICelebrationsService } from './celebrations.interface';
import {
  Celebration,
  CelebrationRequest,
  CelebrationResponse,
  CelebrationsResponse,
  CelebrationUpdateRequest,
} from '#models/db/celebration/celebration.model';
import { ApiRepositoryService } from '#repositories/api-repository/api-repository.service';
import { IntermediaryService, MS_DELAY } from '#services/intermediary/intermediary.service';
import { logger } from '#lib/logger';

@Injectable({
  providedIn: 'root',
})
export class CelebrationsService implements ICelebrationsService {
  path = 'celebrations';
  createCelebrationLoading$ = new Subject<boolean>();
  deleteCelebrationLoading$ = new Subject<boolean>();
  getCelebrationLoading$ = new Subject<boolean>();
  updateCelebrationLoading$ = new Subject<boolean>();
  activeCelebration$ = new BehaviorSubject<Celebration | null>(null);
  celebrations$ = new BehaviorSubject<Celebration[] | null>(null);

  constructor(
    private apiRepository: ApiRepositoryService,
    private intermediaryHandler: IntermediaryService
  ) {}

  async createCelebration(
    request: CelebrationRequest,
    nextFn?: (data: Celebration | null) => void
  ): Promise<void> {
    this.intermediaryHandler.intermediary(
      {
        loading: this.createCelebrationLoading$,
        subject: this.activeCelebration$,
        delay: MS_DELAY,
        nextFn,
      },
      async () => {
        const response = await this.apiRepository.post<CelebrationResponse, CelebrationRequest>(
          { url: this.path },
          request
        );

        logger.log(response, 'CelebrationService.createCelebration -> response');

        return response.details;
      }
    );
  }

  setActiveCelebration(celebration: Celebration | null): void {
    this.activeCelebration$.next(celebration);
  }

  async getCelebrations(
    nextFn?: (data: Celebration[] | null) => void,
    silent?: boolean
  ): Promise<void> {
    this.intermediaryHandler.intermediary(
      {
        loading: !silent ? this.getCelebrationLoading$ : undefined,
        subject: this.celebrations$,
        delay: !silent ? MS_DELAY : undefined,
        nextFn,
      },
      async () => {
        const response = await this.apiRepository.get<CelebrationsResponse>({ url: this.path });

        const celebrations = response.details || [];

        celebrations.sort((a, b) => {
          return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
        });

        logger.log(celebrations, 'CelebrationsService.getCelebrations -> response');

        return celebrations;
      }
    );
  }

  async getCelebrationById(
    id: string,
    nextFn?: (data: Celebration | null) => void,
    silent?: boolean
  ): Promise<void> {
    this.intermediaryHandler.intermediary(
      {
        loading: !silent ? this.getCelebrationLoading$ : undefined,
        subject: this.activeCelebration$,
        delay: !silent ? MS_DELAY : undefined,
        nextFn,
      },
      async () => {
        const response = await this.apiRepository.get<CelebrationResponse>({
          url: `${this.path}/${id}`,
        });

        logger.log(response.details, 'CelebrationsService.getCelebrationById -> response');

        return response.details;
      }
    );
  }

  async updateCelebration(
    request: CelebrationUpdateRequest,
    nextFn?: (data: Celebration | null) => void
  ): Promise<void> {
    this.intermediaryHandler.intermediary(
      {
        loading: this.updateCelebrationLoading$,
        subject: this.activeCelebration$,
        delay: MS_DELAY,
        nextFn,
      },
      async () => {
        const response = await this.apiRepository.patch<
          CelebrationResponse,
          CelebrationUpdateRequest
        >({ url: `${this.path}/${request.id}` }, request);

        logger.log(response, 'CelebrationsService.updateCelebration -> response');

        return response.details;
      }
    );
  }

  async deleteCelebration(id: string, nextFn?: (data: Celebration | null) => void): Promise<void> {
    this.intermediaryHandler.intermediary(
      {
        loading: this.deleteCelebrationLoading$,
        delay: MS_DELAY,
        nextFn,
      },
      async () => {
        const response = await this.apiRepository.delete<CelebrationResponse>({
          url: `${this.path}/${id}`,
        });

        // Update the celebrations list by removing the deleted celebration.
        if (this.celebrations$.value) {
          const updatedCelebrations = this.celebrations$.value.filter(
            (celebration: Celebration) => {
              return celebration.id !== id;
            }
          );

          this.celebrations$.next(updatedCelebrations);
        }

        logger.log(response.details, 'CelebrationsService.deleteCelebration -> response');

        return response.details;
      }
    );
  }

  resetCelebrations() {
    this.celebrations$.next(null);
    this.activeCelebration$.next(null);
    logger.log('complete', 'CelebrationsService.resetCelebrations');
  }
}
